import { Injectable, inject } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

declare global {
  interface Window {
    Pusher: any;
    Echo: Echo<any>;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EchoService {
  private readonly _authService = inject(AuthService);
  private _echo: Echo<any> | null = null;
  private _initializationAttempted = false;

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift();
      return cookieValue ? decodeURIComponent(cookieValue) : null;
    }
    return null;
  }

  /**
   * Initialize Laravel Echo with Pusher.
   * Called on-demand when first channel subscription is requested.
   */
  private initialize(): void {
    if (this._initializationAttempted) {
      return;
    }

    this._initializationAttempted = true;

    // Prüfe ob User eingeloggt ist
    if (!this._authService.isAuthenticated()) {
      console.warn('Echo initialization skipped - user not authenticated');
      return;
    }

    window.Pusher = Pusher;

    // Enable Pusher debug logging in non-production to diagnose WebSocket issues
    if (!environment.production) {
      Pusher.logToConsole = true;
    }

    // Sanctum nutzt Cookies - kein Bearer Token nötig
    // withCredentials: true sendet Cookies automatisch mit
    this._echo = new Echo({
      broadcaster: 'pusher',
      key: environment.pusher.key,
      cluster: environment.pusher.cluster,
      forceTLS: true,
      encrypted: true,
      authEndpoint: `${environment.apiUrl}/broadcasting/auth`,
      auth: {
        headers: {
          'Accept': 'application/json',
          'X-XSRF-TOKEN': this.getCookie('XSRF-TOKEN') || '',
        },
      },
      authorizer: (channel: any) => {
        return {
          authorize: (socketId: string, callback: Function) => {
            fetch(`${environment.apiUrl}/broadcasting/auth`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-XSRF-TOKEN': this.getCookie('XSRF-TOKEN') || '',
              },
              credentials: 'include', // Wichtig: sendet Cookies mit
              body: JSON.stringify({
                socket_id: socketId,
                channel_name: channel.name,
              }),
            })
              .then(async (response) => {
                const text = await response.text();

                if (!response.ok) {
                  throw new Error(`Broadcasting auth failed: ${response.status} - ${text}`);
                }

                if (!text) {
                  throw new Error('Empty response from broadcasting auth');
                }

                try {
                  return JSON.parse(text);
                } catch (e) {
                  throw new Error(`Invalid JSON from broadcasting auth: ${text}`);
                }
              })
              .then((data) => {
                callback(null, data);
              })
              .catch((error) => {
                callback(error);
              });
          },
        };
      },
    });

    window.Echo = this._echo;
    console.log('Echo initialized successfully with Sanctum cookie auth');
  }

  /**
   * Get the Echo instance
   */
  public getEcho(): Echo<any> | null {
    return this._echo;
  }

  /**
   * Get the current Pusher socket ID.
   * Used by the HTTP interceptor to send X-Socket-Id for toOthers() exclusion.
   */
  public socketId(): string | undefined {
    return this._echo?.socketId();
  }

  /**
   * Re-initialize Echo after login.
   * Called explicitly after successful authentication.
   */
  public reinitialize(): void {
    console.log('Echo: Reinitializing after login...');
    this._initializationAttempted = false;
    this._echo = null;
    this.initialize();
  }

  /**
   * Subscribe to a private channel
   */
  public private(channel: string): any {
    // Lazy initialization: initialisiere Echo beim ersten Channel-Subscribe
    if (!this._echo && !this._initializationAttempted) {
      this.initialize();
    }

    if (!this._echo) {
      console.error('Echo initialization failed - cannot subscribe to channel');
      return null;
    }

    return this._echo.private(channel);
  }

  /**
   * Leave a channel
   */
  public leave(channel: string): void {
    if (!this._echo) {
      return;
    }
    this._echo.leave(channel);
  }

  /**
   * Disconnect from all channels
   */
  public disconnect(): void {
    if (this._echo) {
      this._echo.disconnect();
    }
  }
}
